import React, { useEffect, useState, useRef } from "react";
import { Inbox, Send, FileEdit, Trash2, Plus, Star, Sparkles, Loader2, Reply, X, Mail as MailIcon, Search, Tag, CheckSquare, Square, ShieldCheck, Paperclip, Download, Lock, RefreshCw, Link as LinkIcon, CalendarPlus, Mic, MicOff, ChevronLeft, Menu, ChevronDown, StickyNote, ListChecks, Calendar as CalendarIcon, UserPlus, Bookmark, Clock as ClockIcon, File as FileIcon, Image as ImageIcon, Briefcase } from "lucide-react";
import { api, externalMailApi, mailAiApi, meetingsApi } from "../api";
import { mailUnread } from "../mailUnread";
import { useAuth } from "../AuthContext";
import { useIsMobile } from "../useIsMobile";
import { useWM } from "../store";
import { APPS } from "../apps";

const FOLDERS = [
  { id: "inbox-hexa", label: "HEXA Inbox", icon: Inbox, group: "inbox", kind: "hexa" },
  { id: "inbox-external", label: "External Inbox", icon: Inbox, group: "inbox", kind: "external" },
  { id: "unread", label: "Unread", icon: MailIcon, group: "filter" },
  { id: "sent", label: "Sent", icon: Send, group: "sent" },
  { id: "drafts", label: "Drafts", icon: FileEdit, group: "drafts" },
  { id: "starred", label: "Starred", icon: Star, group: "filter" },
  { id: "trash", label: "Trash", icon: Trash2, group: "trash" },
];

const LABEL_COLORS = {
  Important: "#ff7a2e",
  Work: "#3b82f6",
  Personal: "#10b981",
  Updates: "#a855f7",
  Promotions: "#eab308",
};

const colorFor = (label) => LABEL_COLORS[label] || "#71717a";

// Internal HEXA-to-HEXA shorthand: when the address ends with @hexawebos.com we
// only display the username prefixed with @. External addresses keep the full form.
const HEXA_DOMAIN = "@hexawebos.com";
const shortAddr = (addr) => {
  if (!addr || typeof addr !== "string") return addr || "";
  const a = addr.trim();
  return a.toLowerCase().endsWith(HEXA_DOMAIN) ? `@${a.slice(0, -HEXA_DOMAIN.length)}` : a;
};

// Stable per-address color (used for the team-list dots).
const TEAM_DOT_COLORS = ["#60a5fa", "#a78bfa", "#34d399", "#fbbf24", "#fb7185", "#22d3ee", "#f59e0b", "#c084fc"];
const ringFor = (addr) => {
  if (!addr) return "#94a3b8";
  let h = 0;
  for (let i = 0; i < addr.length; i++) h = (h * 31 + addr.charCodeAt(i)) >>> 0;
  return TEAM_DOT_COLORS[h % TEAM_DOT_COLORS.length];
};

export default function Mail() {
  const { user } = useAuth();
  const { open: openApp } = useWM();
  const account = user?.address || "";
  const [accountInfo, setAccountInfo] = useState(null);
  const [folder, setFolder] = useState("inbox-hexa");
  const [label, setLabel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiDrafting, setAiDrafting] = useState(false);
  const [draftMsg, setDraftMsg] = useState("");
  const [summary, setSummary] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [analysisBusy, setAnalysisBusy] = useState(false);
  const [analysisError, setAnalysisError] = useState("");
  const [convertingDeal, setConvertingDeal] = useState(false);
  const [actionMsg, setActionMsg] = useState("");
  const [q, setQ] = useState("");
  const [tasks, setTasks] = useState([]);
  const [thread, setThread] = useState([]);
  const [externalAccounts, setExternalAccounts] = useState([]);
  const [externalOpen, setExternalOpen] = useState(false);
  // Per-bucket unread counts (HEXA + External) for inbox sidebar badges.
  const [unread, setUnread] = useState({ hexa: 0, external: 0, total: 0 });
  // Team members for Business workspaces (empty for individuals).
  const [team, setTeam] = useState([]);
  const [teamFilter, setTeamFilter] = useState(null); // address | null
  const [creating, setCreating] = useState(null); // "task" | "event" | null — linked to the open message
  const isBusiness = user?.account_type === "business" || (user?.role && user.role !== "individual");

  // Mobile stack-view: one column visible at a time.
  const isMobile = useIsMobile();
  const [mobileView, setMobileView] = useState("list"); // 'folders' | 'list' | 'detail'

  // Live unread counts → updates the sidebar badges and any "new mail" flash.
  useEffect(() => {
    const unsub = mailUnread.subscribe((c, evt) => {
      setUnread(c);
      // When a new inbox message lands, refresh the visible list if we are
      // viewing the matching bucket.
      if (evt && evt.type === "mail:new") {
        const cur = currentFolderRef.current;
        if ((cur === "inbox-hexa" && evt.kind === "hexa") ||
            (cur === "inbox-external" && evt.kind === "external")) {
          loadMessagesRef.current?.();
        }
      }
    });
    return () => unsub();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const loadExternal = async () => {
    try { setExternalAccounts(await externalMailApi.list()); } catch { /* ignore */ }
  };
  const loadTeam = async () => {
    if (!isBusiness) { setTeam([]); return; }
    try {
      const { data } = await api.get("/users/directory");
      setTeam(data || []);
    } catch { setTeam([]); }
  };
  useEffect(() => { loadExternal(); loadTeam(); }, [isBusiness]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Support #301778: "Task" and "Event" labels always available, after Promotions.
  const labels = Array.from(new Set([...(accountInfo?.labels || ["Important", "Work", "Personal", "Updates", "Promotions"]), "Task", "Event"]));

  const loadAccountInfo = async () => {
    if (!account) return;
    const { data } = await api.get("/mail/accounts");
    const me = data.find(a => a.address === account);
    setAccountInfo(me);
  };

  const loadMessages = async () => {
    if (!account) return;
    // Map the sidebar folder id to the API "folder" param + bucket filter.
    let apiFolder = folder;
    let externalFilter = null;  // null | true | false
    let virtualFilter = null;   // null | "unread" | "starred"
    if (folder === "inbox-hexa") { apiFolder = "inbox"; externalFilter = false; }
    else if (folder === "inbox-external") { apiFolder = "inbox"; externalFilter = true; }
    else if (folder === "unread")  { apiFolder = "inbox"; virtualFilter = "unread"; }
    else if (folder === "starred") { apiFolder = "inbox"; virtualFilter = "starred"; }
    const params = { account, folder: apiFolder };
    if (label) params.label = label;
    const { data } = await api.get("/mail/messages", { params });
    let filtered = externalFilter === null
      ? data
      : externalFilter
        ? data.filter(m => m.external === true)
        : data.filter(m => !m.external);
    if (virtualFilter === "unread") {
      filtered = filtered.filter(m => !m.read);
    } else if (virtualFilter === "starred") {
      // Also include starred messages from Sent so the user sees everything
      // they marked with a star regardless of location.
      try {
        const { data: sent } = await api.get("/mail/messages", { params: { account, folder: "sent" } });
        filtered = [...filtered, ...(sent || [])].filter(m => m.starred);
        // Sort newest first
        filtered.sort((a, b) => new Date(b.ts || 0) - new Date(a.ts || 0));
      } catch {
        filtered = filtered.filter(m => m.starred);
      }
    }
    setMessages(filtered);
    setSelected(filtered[0] || null);
    setSummary("");
  };

  // Refs the WS-driven mailUnread subscriber uses to reload the current view
  // without re-running the subscribe effect on every render.
  const currentFolderRef = React.useRef(folder);
  const loadMessagesRef = React.useRef(loadMessages);
  useEffect(() => { currentFolderRef.current = folder; }, [folder]);
  useEffect(() => { loadMessagesRef.current = loadMessages; });

  const loadTasks = async () => {
    if (!account) return;
    const { data } = await api.get("/mail/tasks", { params: { account } });
    setTasks(data);
  };

  useEffect(() => { loadAccountInfo(); }, [account]);
  useEffect(() => { loadMessages(); loadTasks(); /* eslint-disable-next-line */ }, [account, folder, label]);

  const open = async (m) => {
    setSelected(m);
    setSummary("");
    setAnalysis(null);
    setAnalysisError("");
    setActionMsg("");
    if (isMobile) setMobileView("detail");
    if (!m.read) {
      await api.put(`/mail/messages/${m.id}`, { read: true });
      setMessages(ms => ms.map(x => x.id === m.id ? { ...x, read: true } : x));
      // Decrement the sidebar / dock badge instantly.
      mailUnread.refresh();
    }
    // Load thread
    if (m.thread_id) {
      try {
        const { data } = await api.get(`/mail/threads/${m.thread_id}`, { params: { account } });
        setThread(data);
      } catch { setThread([m]); }
    } else {
      setThread([m]);
    }
    // Auto-analyze in background (uses cached result if present)
    setAnalysisBusy(true);
    setAnalysisError("");
    try {
      const a = await mailAiApi.analyze(m.id, false);
      setAnalysis(a);
    } catch (e) {
      setAnalysisError(e?.response?.data?.detail || "AI analysis unavailable");
    }
    setAnalysisBusy(false);
  };

  const regenAnalysis = async () => {
    if (!selected) return;
    setAnalysisBusy(true);
    setAnalysisError("");
    try {
      const a = await mailAiApi.analyze(selected.id, true);
      setAnalysis(a);
    } catch (e) {
      setAnalysisError(e?.response?.data?.detail || "AI analysis unavailable");
    }
    setAnalysisBusy(false);
  };

  const doExtractTasks = async () => {
    if (!selected) return;
    setActionMsg("Creating tasks…");
    try {
      const r = await mailAiApi.extractTasks(selected.id);
      setActionMsg(r.message || `Created ${r.created?.length || 0} tasks`);
    } catch (e) { setActionMsg(e?.response?.data?.detail || "Extract failed"); }
  };

  const doScheduleMeeting = async () => {
    if (!selected) return;
    setActionMsg("Scheduling meeting…");
    try {
      const r = await mailAiApi.scheduleMeeting(selected.id, {});
      setActionMsg(`Meeting scheduled: ${r.meeting?.title} at ${r.meeting?.scheduled_at}`);
    } catch (e) { setActionMsg(e?.response?.data?.detail || "Schedule failed"); }
  };

  /**
   * One-click "Convert to Deal" — Novo extracts the counterparty, drafts a
   * deal title + notes from the email, upserts a CRM contact, creates a lead
   * deal linked to the message, then opens the CRM app so the user lands on
   * the newly-created deal.
   */
  const convertToDeal = async (msg) => {
    if (!msg || convertingDeal) return;
    setConvertingDeal(true);
    setActionMsg("Converting to deal…");
    try {
      const { data } = await api.post(`/crm/from-mail/${msg.id}`);
      const dealTitle = data?.deal?.title || "Deal";
      const isNew = data?.created_contact;
      setActionMsg(`✓ Deal "${dealTitle}" created${isNew ? " · new contact added" : " · existing contact linked"}`);
      // Jump straight to the CRM app so the user sees the new deal
      try {
        const crmApp = APPS.find((a) => a.id === "crm");
        if (crmApp) openApp(crmApp);
      } catch { /* fallback: ignore */ }
    } catch (e) {
      setActionMsg(e?.response?.data?.detail || "Convert failed");
    } finally {
      setConvertingDeal(false);
    }
  };

  const star = async (m) => {
    const next = !m.starred;
    await api.put(`/mail/messages/${m.id}`, { starred: next });
    setMessages(ms => ms.map(x => x.id === m.id ? { ...x, starred: next } : x));
    if (selected?.id === m.id) setSelected({ ...m, starred: next });
  };

  const trash = async (m) => { await api.delete(`/mail/messages/${m.id}`); loadMessages(); };

  const toggleLabel = async (m, lbl) => {
    const current = m.labels || [];
    const next = current.includes(lbl) ? current.filter(x => x !== lbl) : [...current, lbl];
    await api.put(`/mail/messages/${m.id}`, { labels: next });
    setMessages(ms => ms.map(x => x.id === m.id ? { ...x, labels: next } : x));
    if (selected?.id === m.id) setSelected({ ...m, labels: next });
  };

  // Support #301778: clicking Task / Event on an email also files it under that label.
  const ensureLabel = async (m, lbl) => {
    if (!m || (m.labels || []).includes(lbl)) return;
    try { await toggleLabel(m, lbl); } catch { /* noop */ }
  };

  const summarize = async () => {
    if (!selected) return;
    setAiBusy(true);
    try {
      const { data } = await api.post("/mail/ai/summarize", { body: selected.body });
      setSummary(data.summary);
    } catch { setSummary("AI unavailable."); }
    setAiBusy(false);
  };

  const addLabel = async () => {
    const name = prompt("New label name");
    if (!name) return;
    await api.post("/mail/labels", { account, label: name });
    loadAccountInfo();
  };

  const removeLabel = async (lbl) => {
    if (!confirm(`Delete label "${lbl}"?`)) return;
    await api.delete("/mail/labels", { params: { account, label: lbl } });
    if (label === lbl) setLabel(null);
    loadAccountInfo();
    loadMessages();
  };

  const addTask = async () => {
    const text = prompt("Task / reminder");
    if (!text) return;
    await api.post("/mail/tasks", { account, text, priority: "normal" });
    loadTasks();
  };

  const toggleTask = async (t) => {
    await api.put(`/mail/tasks/${t.id}`, { done: !t.done });
    loadTasks();
  };

  const delTask = async (t) => { await api.delete(`/mail/tasks/${t.id}`); loadTasks(); };

  const filtered = messages.filter(m => {
    if (q) {
      const ql = q.toLowerCase();
      if (!(m.subject.toLowerCase().includes(ql) || m.body.toLowerCase().includes(ql) || m.from_addr.toLowerCase().includes(ql))) return false;
    }
    if (teamFilter) {
      const f = teamFilter.toLowerCase();
      const fa = (m.from_addr || "").toLowerCase();
      const ta = (m.to_addr || "").toLowerCase();
      if (fa !== f && ta !== f) return false;
    }
    return true;
  });

  return (
    <div className="h-full flex flex-col md:flex-row" data-testid="app-mail">
      {/* Mobile-only top toolbar: hamburger / back chevron */}
      {isMobile && (
        <div
          data-testid="mail-mobile-toolbar"
          className="flex items-center gap-2 px-3 py-2 border-b border-white/10 bg-black/40"
        >
          {mobileView === "detail" ? (
            <button
              data-testid="mail-mobile-back"
              onClick={() => setMobileView("list")}
              className="p-1.5 -ml-1 rounded hover:bg-white/8 text-white/85"
            >
              <ChevronLeft size={18} />
            </button>
          ) : mobileView === "folders" ? (
            <button
              data-testid="mail-mobile-back"
              onClick={() => setMobileView("list")}
              className="p-1.5 -ml-1 rounded hover:bg-white/8 text-white/85"
            >
              <ChevronLeft size={18} />
            </button>
          ) : (
            <button
              data-testid="mail-mobile-folders"
              onClick={() => setMobileView("folders")}
              className="p-1.5 -ml-1 rounded hover:bg-white/8 text-white/85"
            >
              <Menu size={18} />
            </button>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">
              {mobileView === "detail"
                ? (selected?.subject || "Message")
                : mobileView === "folders"
                  ? "Mailboxes"
                  : (FOLDERS.find(f => f.id === folder)?.label || "Inbox")}
            </div>
            {mobileView === "list" && (unread.hexa > 0 || unread.external > 0) && (
              <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55">
                {folder === "inbox-hexa" && unread.hexa > 0 && `${unread.hexa} unread`}
                {folder === "inbox-external" && unread.external > 0 && `${unread.external} unread`}
              </div>
            )}
          </div>
          {mobileView === "list" && (
            <button
              data-testid="mail-compose-mobile"
              onClick={() => setComposeOpen(true)}
              className="btn-accent text-xs flex items-center justify-center gap-1 px-2.5 py-1.5 rounded"
            >
              <Plus size={12} /> New
            </button>
          )}
        </div>
      )}

      {/* Sidebar */}
      <div
        className={`${isMobile ? (mobileView === "folders" ? "flex" : "hidden") : "flex"} w-full md:w-60 border-r border-white/10 flex-col bg-black/30`}
      >
        <div className="p-3">
          <button
            data-testid="mail-compose"
            onClick={() => setComposeOpen(true)}
            className="btn-accent text-xs flex items-center justify-center gap-1 px-2.5 py-1.5 rounded w-full"
          >
            <Plus size={12} /> New Mail
          </button>
        </div>
        <div className="px-2 overflow-y-auto flex-1">
          {FOLDERS.map(f => {
            const Icon = f.icon;
            const isHexa = f.kind === "hexa";
            const isExternal = f.kind === "external";
            const count = isHexa ? unread.hexa : isExternal ? unread.external : 0;
            const accent = isHexa ? "#ff7a2e" : isExternal ? "#8b5cf6" : null;
            const active = folder === f.id && !label;
            return (
              <button
                key={f.id}
                data-testid={`mail-folder-${f.id}`}
                onClick={() => { setFolder(f.id); setLabel(null); if (isMobile) setMobileView("list"); }}
                className={`w-full text-left px-3 py-1.5 rounded mb-0.5 text-sm flex items-center gap-2 ${active ? "bg-white/10" : "hover:bg-white/5"}`}
                style={active && accent ? { boxShadow: `inset 2px 0 0 ${accent}` } : undefined}
              >
                <Icon size={13} style={{ color: accent || undefined }} className={!accent ? "text-white/60" : ""} />
                <span className="flex-1">{f.label}</span>
                {(isHexa || isExternal) && count > 0 && (
                  <span
                    data-testid={`mail-folder-${f.id}-badge`}
                    className="mono text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none"
                    style={{
                      background: accent,
                      color: "#0e1a22",
                      minWidth: 18,
                      textAlign: "center",
                      boxShadow: `0 0 10px ${accent}66`,
                    }}
                  >
                    {count > 99 ? "99+" : count}
                  </span>
                )}
              </button>
            );
          })}

          {/* Team Email — business workspaces only */}
          {isBusiness && (
            <div data-testid="mail-team-section" className="mt-4">
              <div className="px-3 text-[10px] uppercase tracking-widest text-white/40 flex items-center justify-between">
                <span>Team Email</span>
                {teamFilter && (
                  <button
                    data-testid="mail-team-clear"
                    onClick={() => setTeamFilter(null)}
                    className="text-white/55 hover:text-white"
                    title="Clear team filter"
                  >
                    <X size={10} />
                  </button>
                )}
              </div>
              <div className="mt-1.5 px-1">
                {team.length === 0 && (
                  <div className="text-[10px] text-white/30 px-2">No teammates yet</div>
                )}
                {team.map((t) => {
                  const username = (t.username || (t.address || "").split("@")[0]) || "user";
                  const handle = `@${username}`;
                  const isActive = teamFilter === t.address;
                  const dot = ringFor(t.address);
                  return (
                    <button
                      key={t.address}
                      data-testid={`mail-team-${username}`}
                      onClick={() => setTeamFilter(isActive ? null : t.address)}
                      title={t.address}
                      className={`w-full text-left px-3 py-1.5 rounded mb-0.5 text-xs flex items-center gap-2 ${isActive ? "bg-white/10 ring-1 ring-white/15" : "hover:bg-white/5"}`}
                    >
                      <span
                        className="w-1.5 h-1.5 rounded-full inline-block"
                        style={{ background: dot, boxShadow: `0 0 8px ${dot}99` }}
                      />
                      <span className="mono truncate" style={{ color: isActive ? "#fff" : "rgba(255,255,255,0.85)" }}>
                        {handle}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-4 px-3 text-[10px] uppercase tracking-widest text-white/40 flex items-center justify-between">
            Labels
            <button onClick={addLabel} data-testid="mail-add-label" className="text-white/60 hover:text-white"><Plus size={10} /></button>
          </div>
          {labels.map(lbl => (
            <div key={lbl} className="group flex items-center">
              <button
                data-testid={`mail-label-${lbl}`}
                onClick={() => { setLabel(label === lbl ? null : lbl); setFolder("inbox-hexa"); }}
                className={`flex-1 text-left px-3 py-1.5 rounded text-xs flex items-center gap-2 ${label === lbl ? "bg-white/10" : "hover:bg-white/5"}`}
              >
                <Tag size={10} color={colorFor(lbl)} />
                {lbl}
              </button>
              <button onClick={() => removeLabel(lbl)} className="opacity-0 group-hover:opacity-100 pr-2"><X size={9} className="text-red-400" /></button>
            </div>
          ))}

          {/* Tasks / Reminders */}
          <div className="mt-4 px-3 text-[10px] uppercase tracking-widest text-white/40 flex items-center justify-between">
            Tasks & Reminders
            <button onClick={addTask} data-testid="mail-add-task" className="text-white/60 hover:text-white"><Plus size={10} /></button>
          </div>
          <div className="px-2 space-y-1">
            {tasks.length === 0 && <div className="text-[10px] text-white/30 px-2">No tasks</div>}
            {tasks.map(t => (
              <div key={t.id} className="group flex items-center gap-1.5 text-xs px-1">
                <button onClick={() => toggleTask(t)} data-testid={`task-toggle-${t.id}`}>
                  {t.done ? <CheckSquare size={12} color="#ff7a2e" /> : <Square size={12} className="text-white/50" />}
                </button>
                <span className={`flex-1 truncate ${t.done ? "line-through text-white/40" : ""}`}>{t.text}</span>
                <button onClick={() => delTask(t)} className="opacity-0 group-hover:opacity-100"><X size={10} className="text-red-400" /></button>
              </div>
            ))}
          </div>
        </div>

        <div className="p-2 border-t border-white/10">
          <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1 px-2 flex items-center justify-between">
            <span>External Mailboxes</span>
            <button
              data-testid="mail-connect-external"
              onClick={() => setExternalOpen(true)}
              className="text-[#3b82f6] hover:brightness-110 mono"
              title="Connect Gmail / Outlook / IMAP"
            >+ Connect</button>
          </div>
          {externalAccounts.length === 0 ? (
            <div className="px-2 py-1.5 text-[9px] text-white/30 mono">// none connected</div>
          ) : (
            externalAccounts.map(ea => (
              <div key={ea.id} data-testid={`ext-account-${ea.id}`} className="px-2 py-1.5 rounded bg-[#8b5cf6]/8 flex items-center gap-1.5 mb-1 group">
                <span
                  className="inline-block"
                  style={{
                    width: 10, height: 11,
                    clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
                    background: ea.provider === "gmail" ? "#ea4335" : ea.provider === "outlook" ? "#0078d4" : ea.provider === "yahoo" ? "#7e3ff2" : "#8b5cf6",
                  }}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] truncate text-[#c7c1ff]">{ea.email}</div>
                  <div className="text-[8px] text-white/40 truncate uppercase tracking-widest mono">{ea.provider_label}</div>
                </div>
                <button
                  onClick={async () => { await externalMailApi.sync(ea.id); loadMessages(); loadExternal(); }}
                  className="text-[#8b5cf6] opacity-0 group-hover:opacity-100"
                  title="Sync new email"
                  data-testid={`ext-sync-${ea.id}`}
                ><RefreshCw size={9} /></button>
                <button
                  onClick={async () => { await externalMailApi.disconnect(ea.id); loadExternal(); loadMessages(); }}
                  className="text-red-400 opacity-0 group-hover:opacity-100"
                  title="Disconnect"
                  data-testid={`ext-disconnect-${ea.id}`}
                ><X size={9} /></button>
              </div>
            ))
          )}
        </div>

        <div className="p-2 border-t border-white/10">
          <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1 px-2 flex items-center justify-between">
            Signed in as
          </div>
          <div className="px-2 py-2 rounded bg-[#ff7a2e]/15 flex items-center gap-1.5">
            <MailIcon size={10} className="text-[#ffb27a]" />
            <div className="flex-1 min-w-0">
              <div className="text-xs truncate text-[#ffb27a]">{shortAddr(account)}</div>
              <div className="text-[9px] text-white/40 truncate">{user?.name}</div>
            </div>
          </div>
          <div className="px-2 mt-1.5 text-[9px] text-white/40 flex items-center gap-1">
            <ShieldCheck size={9} /> HEXA ID · @hexawebos.com only
          </div>
        </div>
      </div>

      {/* Message list */}
      <div className={`${isMobile ? (mobileView === "list" ? "flex" : "hidden") : "flex"} w-full md:w-80 border-r border-white/10 flex-col min-h-0`}>
        <div className="px-3 py-2 border-b border-white/10 flex items-center gap-2">
          <Search size={12} className="text-white/40" />
          <input
            data-testid="mail-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search"
            className="flex-1 bg-transparent outline-none text-xs"
          />
        </div>
        {/* Team filter banner — shown when a teammate is selected from the sidebar */}
        {teamFilter && (
          <div
            data-testid="mail-team-banner"
            className="px-3 py-2 border-b border-white/10 flex items-center gap-2"
            style={{ background: "rgba(167,139,250,0.08)" }}
          >
            <span
              className="w-2 h-2 rounded-full inline-block"
              style={{ background: ringFor(teamFilter), boxShadow: `0 0 8px ${ringFor(teamFilter)}aa` }}
            />
            <div className="flex flex-col min-w-0">
              <span className="mono text-[11px] text-white/90 truncate">{shortAddr(teamFilter)}</span>
              <span className="mono text-[9px] text-white/45 truncate">{teamFilter}</span>
            </div>
            <button
              data-testid="mail-team-banner-clear"
              onClick={() => setTeamFilter(null)}
              className="ml-auto text-white/55 hover:text-white"
              title="Clear team filter"
            >
              <X size={11} />
            </button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 && <div className="text-center text-white/40 text-xs py-12">No messages</div>}
          {filtered.map(m => (
            <button
              key={m.id}
              data-testid={`mail-msg-${m.id}`}
              onClick={() => open(m)}
              className={`w-full text-left px-3 py-2 border-b border-white/5 ${selected?.id === m.id ? "bg-white/10" : "hover:bg-white/5"}`}
            >
              <div className="flex items-center gap-2">
                {!m.read && <div className="w-1.5 h-1.5 rounded-full bg-[#ff7a2e]" />}
                {m.external && (
                  <span
                    className="inline-block flex-shrink-0"
                    title={`${m.external_provider} · ${m.external_email}`}
                    style={{
                      width: 8, height: 9,
                      clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
                      background: m.external_provider === "gmail" ? "#ea4335" : m.external_provider === "outlook" ? "#0078d4" : m.external_provider === "yahoo" ? "#7e3ff2" : "#8b5cf6",
                    }}
                  />
                )}
                <span className={`text-xs truncate flex-1 ${m.read ? "text-white/70" : "font-semibold"}`}>{m.from_name || shortAddr(m.from_addr)}</span>
                <span className="text-[10px] text-white/40 ml-2">{new Date(m.ts).toLocaleDateString([], { month: "short", day: "numeric" })}</span>
              </div>
              <div className={`text-xs truncate mt-0.5 ${m.read ? "text-white/60" : ""}`}>{m.subject}</div>
              <div className="text-[10px] text-white/40 truncate flex items-center gap-1">
                {m.attachments && m.attachments.length > 0 && (
                  <span className="flex items-center gap-0.5 text-[#ff7a2e]"><Paperclip size={8} />{m.attachments.length}</span>
                )}
                {m.encrypted && <Lock size={8} className="text-emerald-400" />}
                <span className="truncate">{m.body.slice(0, 60)}</span>
              </div>
              {m.labels && m.labels.length > 0 && (
                <div className="flex gap-1 mt-1">
                  {m.labels.map(l => (
                    <span key={l} className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: `${colorFor(l)}22`, color: colorFor(l) }}>{l}</span>
                  ))}
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Message viewer */}
      <div className={`${isMobile ? (mobileView === "detail" ? "flex" : "hidden") : "flex"} flex-1 flex-col min-h-0`}>
        {selected ? (
          <>
            <div className="px-4 py-3 border-b border-white/10">
              <div className="flex items-center justify-between mb-1">
                <div className="text-base font-medium tracking-tight">{selected.subject}</div>
                <div className="flex items-center gap-1">
                  <button
                    data-testid="mail-convert-to-deal"
                    onClick={() => convertToDeal(selected)}
                    disabled={convertingDeal}
                    title="Convert this email into a CRM contact + deal"
                    className="btn-ghost text-[10.5px] px-2 py-1 flex items-center gap-1 border"
                    style={{
                      borderColor: "rgba(124,58,237,0.6)",
                      color: "#7c3aed",
                      background: "linear-gradient(120deg, rgba(124,58,237,0.14), rgba(56,189,248,0.10))",
                    }}
                  >
                    {convertingDeal ? <Loader2 size={11} className="animate-spin" /> : <Briefcase size={11} />}
                    <span className="hidden sm:inline">Convert to Deal</span>
                    <span className="sm:hidden">Deal</span>
                  </button>
                  <button onClick={() => star(selected)} data-testid="mail-star" className="btn-ghost mail-ghost-fix p-1.5">
                    <Star size={14} fill={selected.starred ? "#f59e0b" : "none"} stroke={selected.starred ? "#f59e0b" : "#475569"} />
                  </button>
                  <button onClick={() => trash(selected)} data-testid="mail-trash" className="btn-ghost mail-ghost-fix p-1.5"><Trash2 size={14} stroke="#475569" /></button>
                </div>
              </div>
              <div className="text-xs text-white/60">
                <span className="font-medium text-white/80">{selected.from_name || shortAddr(selected.from_addr)}</span>
                <span className="text-white/40"> &lt;{shortAddr(selected.from_addr)}&gt;</span>
                <span className="text-white/40"> → {shortAddr(selected.to_addr)}</span>
                <span className="ml-2 text-white/40">{new Date(selected.ts).toLocaleString()}</span>
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {labels.filter(l => l !== "Task" && l !== "Event").map(l => (
                  <button
                    key={l}
                    onClick={() => toggleLabel(selected, l)}
                    data-testid={`mail-toggle-label-${l}`}
                    className="text-[10px] px-2 py-0.5 rounded-full border"
                    style={{
                      borderColor: colorFor(l),
                      background: (selected.labels || []).includes(l) ? `${colorFor(l)}33` : "transparent",
                      color: colorFor(l),
                    }}
                  >
                    {(selected.labels || []).includes(l) ? "✓ " : ""}{l}
                  </button>
                ))}
                <button
                  data-testid="mail-chip-add-task"
                  onClick={() => { setCreating("task"); ensureLabel(selected, "Task"); }}
                  className="text-[10px] px-2 py-0.5 rounded-full border flex items-center gap-1"
                  style={{ borderColor: "#34d399", color: "#34d399", background: "transparent" }}
                >
                  <ListChecks size={10} /> Task
                </button>
                <button
                  data-testid="mail-chip-add-event"
                  onClick={() => { setCreating("event"); ensureLabel(selected, "Event"); }}
                  className="text-[10px] px-2 py-0.5 rounded-full border flex items-center gap-1"
                  style={{ borderColor: "#a78bfa", color: "#a78bfa", background: "transparent" }}
                >
                  <CalendarIcon size={10} /> Event
                </button>
              </div>
            </div>

            <div className="flex-1 flex flex-col md:flex-row overflow-hidden min-h-0">
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                {thread.length > 1 && (
                  <div className="text-[10px] uppercase tracking-widest text-white/40 flex items-center gap-1">
                    <Reply size={10} /> {thread.length}-message conversation
                  </div>
                )}
                {(thread.length ? thread : [selected]).map((m, i) => (
                  <div key={m.id} className={`rounded-lg ${i === (thread.length - 1) ? "" : "opacity-90"}`}>
                    {thread.length > 1 && (
                      <div className="text-[11px] text-white/60 mb-1 flex items-center gap-2">
                        <span className="font-medium text-white/80">{m.from_name || shortAddr(m.from_addr)}</span>
                        <span className="text-white/40">{new Date(m.ts).toLocaleString()}</span>
                        {m.encrypted && <span className="text-emerald-400 flex items-center gap-0.5"><Lock size={9} /> encrypted</span>}
                      </div>
                    )}
                    <div className="text-sm whitespace-pre-wrap leading-relaxed text-white/90">{m.body}</div>
                    {m.attachments && m.attachments.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {m.attachments.map(a => (
                          <a
                            key={a.id}
                            href={a.data_b64.startsWith("data:") ? a.data_b64 : `data:${a.mime};base64,${a.data_b64}`}
                            download={a.name}
                            data-testid={`mail-attachment-${a.id}`}
                            className="flex items-center gap-2 px-3 py-2 rounded border border-white/10 bg-white/5 hover:bg-white/10 text-xs"
                          >
                            <Paperclip size={11} className="text-[#ff7a2e]" />
                            <span className="max-w-[180px] truncate">{a.name}</span>
                            <span className="text-white/40 text-[10px]">{Math.round((a.size || 0)/1024)} KB</span>
                            <Download size={11} className="text-white/60" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {/* Linked tasks / events for this email — inline create + list */}
                <LinkedItemsPanel messageId={selected.id} openApp={openApp} creating={creating} setCreating={setCreating} />
              </div>
              <div className="w-full md:w-80 md:border-l border-t md:border-t-0 border-white/10 bg-black/30 flex flex-col overflow-y-auto mail-ai-panel">
                <div className="px-3 py-2 border-b border-white/10 flex items-center justify-between text-xs uppercase tracking-widest text-white/40">
                  <span className="flex items-center gap-1.5"><Sparkles size={12} color="#ff7a2e" /> AI Insight</span>
                  <button
                    data-testid="mail-ai-regen"
                    onClick={regenAnalysis}
                    disabled={analysisBusy}
                    className="text-[10px] mono hover:text-white/80"
                    title="Re-analyze with AI"
                  >
                    {analysisBusy ? <Loader2 size={11} className="animate-spin" /> : "regen"}
                  </button>
                </div>

                {/* AI Insight summary card */}
                {analysisBusy && !analysis && (
                  <div className="p-3 text-[11px] mono text-white/45 flex items-center gap-1.5">
                    <Loader2 size={11} className="animate-spin" /> Reading email…
                  </div>
                )}
                {!analysisBusy && analysisError && (
                  <div className="p-3 text-[11px] space-y-2" data-testid="mail-ai-insight-error">
                    <div className="text-[#f87171]">{analysisError}</div>
                    <button
                      data-testid="mail-ai-insight-retry"
                      onClick={regenAnalysis}
                      className="btn-ghost mail-ghost-fix text-[10px] flex items-center gap-1"
                    >
                      <RefreshCw size={10} /> Retry
                    </button>
                  </div>
                )}
                {analysis && (
                  <div className="p-3 space-y-2.5" data-testid="mail-ai-insight">
                    {/* Intent badge */}
                    <div className="flex items-center gap-1.5">
                      <span
                        className="text-[9px] mono uppercase tracking-widest px-1.5 py-0.5 rounded-full"
                        style={{
                          background: {
                            urgent: "rgba(248,113,113,0.18)", meeting: "rgba(96,165,250,0.18)",
                            task: "rgba(52,211,153,0.18)", request: "rgba(251,191,36,0.18)",
                            invoice: "rgba(167,139,250,0.18)", informational: "rgba(148,163,184,0.18)",
                          }[analysis.intent] || "rgba(148,163,184,0.18)",
                          color: {
                            urgent: "#f87171", meeting: "#60a5fa", task: "#34d399",
                            request: "#fbbf24", invoice: "#a78bfa", informational: "#94a3b8",
                          }[analysis.intent] || "#94a3b8",
                        }}
                      >
                        {analysis.intent}
                      </span>
                    </div>

                    {/* Purpose / Key Info / Action */}
                    <div className="text-[11px] leading-relaxed space-y-1.5">
                      <div>
                        <div className="text-[9px] mono uppercase tracking-widest text-white/40">Purpose</div>
                        <div className="text-white/85">{analysis.summary?.purpose || "—"}</div>
                      </div>
                      <div>
                        <div className="text-[9px] mono uppercase tracking-widest text-white/40">Key Info</div>
                        <div className="text-white/80">{analysis.summary?.key_info || "—"}</div>
                      </div>
                      <div>
                        <div className="text-[9px] mono uppercase tracking-widest text-white/40">Action</div>
                        <div className="text-white/85">{analysis.summary?.action || "—"}</div>
                      </div>
                    </div>

                    {/* Entities as highlight pills */}
                    {((analysis.entities?.dates || []).length + (analysis.entities?.names || []).length + (analysis.entities?.tasks || []).length) > 0 && (
                      <div className="space-y-1.5">
                        {(analysis.entities?.dates || []).length > 0 && (
                          <div className="flex flex-wrap gap-1 items-center">
                            <span className="text-[9px] mono uppercase tracking-widest text-white/40 mr-1">📅</span>
                            {analysis.entities.dates.map((d, i) => (
                              <span key={`d${i}`} className="text-[10px] px-1.5 py-0.5 rounded-full mono"
                                style={{ background: "rgba(96,165,250,0.15)", color: "#60a5fa" }}>{d}</span>
                            ))}
                          </div>
                        )}
                        {(analysis.entities?.names || []).length > 0 && (
                          <div className="flex flex-wrap gap-1 items-center">
                            <span className="text-[9px] mono uppercase tracking-widest text-white/40 mr-1">👤</span>
                            {analysis.entities.names.map((n, i) => (
                              <span key={`n${i}`} className="text-[10px] px-1.5 py-0.5 rounded-full mono"
                                style={{ background: "rgba(167,139,250,0.15)", color: "#a78bfa" }}>{n}</span>
                            ))}
                          </div>
                        )}
                        {(analysis.entities?.tasks || []).length > 0 && (
                          <div className="flex flex-wrap gap-1 items-start">
                            <span className="text-[9px] mono uppercase tracking-widest text-white/40 mr-1">✓</span>
                            <div className="flex-1 flex flex-col gap-1">
                              {analysis.entities.tasks.map((t, i) => (
                                <span key={`t${i}`} className="text-[10px] px-1.5 py-0.5 rounded mono"
                                  style={{ background: "rgba(52,211,153,0.12)", color: "#34d399" }}>{t}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Action buttons */}
                <div className="px-3 pb-2 flex flex-col gap-1.5">
                  <button data-testid="mail-ai-summarize" disabled={aiBusy} onClick={summarize} className="btn-ghost mail-ghost-fix text-xs flex items-center gap-1">
                    {aiBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Summarize
                  </button>
                  <button
                    data-testid="mail-ai-reply-smart"
                    onClick={() => setComposeOpen({ replyTo: selected, prefill: analysis?.auto_draft || "" })}
                    className="btn-accent text-xs flex items-center gap-1"
                  >
                    <Reply size={12} /> Reply Smart
                  </button>
                  <button
                    data-testid="mail-ai-draft-approval"
                    disabled={aiDrafting}
                    onClick={async () => {
                      setAiDrafting(true);
                      setDraftMsg("");
                      try {
                        await api.post(`/mail/messages/${selected.id}/ai-draft`);
                        setDraftMsg("✓ AI draft queued in Approvals");
                      } catch {
                        setDraftMsg("× draft failed");
                      }
                      setAiDrafting(false);
                      setTimeout(() => setDraftMsg(""), 5000);
                    }}
                    className="btn-ghost mail-ghost-fix text-xs flex items-center gap-1"
                  >
                    {aiDrafting ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Approve Draft
                  </button>
                  {(analysis?.suggested_actions || []).includes("extract_tasks") && (
                    <button data-testid="mail-ai-extract-tasks" onClick={doExtractTasks} className="btn-ghost mail-ghost-fix text-xs flex items-center gap-1" style={{ borderColor: "rgba(52,211,153,0.45)", color: "#34d399" }}>
                      <CheckSquare size={12} /> Extract Tasks
                    </button>
                  )}
                  {(analysis?.suggested_actions || []).includes("schedule_meeting") && (
                    <button data-testid="mail-ai-schedule" onClick={doScheduleMeeting} className="btn-ghost mail-ghost-fix text-xs flex items-center gap-1" style={{ borderColor: "rgba(96,165,250,0.45)", color: "#60a5fa" }}>
                      <CalendarPlus size={12} /> Schedule Meeting
                    </button>
                  )}
                  {actionMsg && <div className="text-[10px] mono text-[#fbbf24] mt-1">{actionMsg}</div>}
                  {draftMsg && (
                    <div className="text-[10px] mono text-[#3b82f6] uppercase tracking-widest">{draftMsg}</div>
                  )}
                </div>

                {/* Auto-draft preview card */}
                {analysis?.auto_draft && (
                  <div className="mx-3 mb-3 rounded border border-[#a78bfa]/30 bg-[#0c1224]/60 mail-autodraft">
                    <div className="px-2 py-1 flex items-center justify-between border-b border-white/8 text-[9px] mono uppercase tracking-widest text-[#a78bfa]">
                      <span>Auto-draft</span>
                      <span className="text-white/30">smart mode</span>
                    </div>
                    <div className="px-2 py-2 text-[11px] whitespace-pre-wrap text-white/80 leading-relaxed">
                      {analysis.auto_draft}
                    </div>
                    <div className="flex gap-1 px-2 pb-2">
                      <button
                        data-testid="mail-autodraft-send"
                        onClick={() => setComposeOpen({ replyTo: selected, prefill: analysis.auto_draft, autoFocus: false })}
                        className="btn-accent text-[10px] flex-1 flex items-center justify-center gap-1"
                      >
                        <Send size={10} /> Send
                      </button>
                      <button
                        data-testid="mail-autodraft-edit"
                        onClick={() => setComposeOpen({ replyTo: selected, prefill: analysis.auto_draft, autoFocus: true })}
                        className="btn-ghost mail-ghost-fix text-[10px] flex-1"
                      >
                        Edit
                      </button>
                      <button
                        data-testid="mail-autodraft-regen"
                        disabled={analysisBusy}
                        onClick={regenAnalysis}
                        className="btn-ghost mail-ghost-fix text-[10px] flex-1 flex items-center justify-center gap-1"
                      >
                        {analysisBusy ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                      </button>
                    </div>
                  </div>
                )}

                {summary && (
                  <div className="mx-3 mb-3 p-2 text-xs whitespace-pre-wrap bg-white/5 border border-white/10 rounded">{summary}</div>
                )}

                {/* External reply panel (Gmail/Outlook/etc) */}
                {selected.external && (
                  <ExternalReplyPanel msg={selected} onSent={() => loadMessages()} />
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-white/40 text-sm">Select a message</div>
        )}
      </div>

      {composeOpen && (
        <Compose
          account={account}
          replyTo={composeOpen.replyTo}
          prefill={composeOpen.prefill}
          externalAccounts={externalAccounts}
          onClose={() => setComposeOpen(false)}
          onSent={() => { setComposeOpen(false); loadMessages(); }}
        />
      )}

      {externalOpen && (
        <ConnectExternal
          onClose={() => setExternalOpen(false)}
          onConnected={() => { setExternalOpen(false); loadExternal(); loadMessages(); }}
        />
      )}
    </div>
  );
}

function ConnectExternal({ onClose, onConnected }) {
  const [provider, setProvider] = useState("gmail");
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [imapHost, setImapHost] = useState("");
  const [imapPort, setImapPort] = useState("993");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const providers = [
    { id: "gmail",  label: "Gmail",       color: "#ea4335" },
    { id: "custom", label: "Custom IMAP", color: "#8b5cf6" },
  ];

  const submit = async (e) => {
    e?.preventDefault();
    if (!email.includes("@")) { setErr("Enter a valid email address"); return; }
    if (provider === "custom" && !imapHost.trim()) { setErr("Enter your mail server's IMAP host (e.g. imap.yourdomain.com)"); return; }
    setBusy(true); setErr("");
    try {
      await externalMailApi.connect(provider, email, pwd, email.split("@")[0], imapHost.trim(), Number(imapPort) || 993);
      onConnected?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to connect");
    }
    setBusy(false);
  };

  return (
    <div className="absolute inset-0 z-[50] flex items-center justify-center" style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(8px)" }} onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="panel-strong w-[460px] max-w-[92%] p-5"
        style={{ clipPath: "polygon(0 0, calc(100% - 14px) 0, 100% 14px, 100% 100%, 14px 100%, 0 calc(100% - 14px))" }}
        data-testid="connect-external-modal"
      >
        <div className="flex items-center gap-2 mb-4">
          <LinkIcon size={14} color="#3b82f6" />
          <div className="mono text-[11px] uppercase tracking-[0.3em] text-[#3b82f6]">// connect external mailbox</div>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-4">
          {providers.map(p => (
            <button
              key={p.id}
              data-testid={`provider-${p.id}`}
              onClick={() => setProvider(p.id)}
              className={`p-3 mono text-[10px] uppercase tracking-widest transition-colors ${provider === p.id ? "" : "hover:bg-[#8b5cf6]/5"}`}
              style={{
                background: provider === p.id ? `${p.color}22` : "transparent",
                border: `1px solid ${provider === p.id ? p.color + "88" : "rgba(139,92,246,0.18)"}`,
                color: provider === p.id ? p.color : "#c7c1ff",
              }}
            >
              {p.label}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55 mb-1">email address</div>
            <input
              data-testid="external-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={provider === "gmail" ? "you@gmail.com" : "you@yourdomain.com"}
              className="w-full bg-[#0e1a22] border border-[#8b5cf6]/25 px-3 py-2 text-sm outline-none focus:border-[#8b5cf6]/60 mono"
              autoFocus
            />
          </div>

          {provider === "custom" && (
            <>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55 mb-1">imap host</div>
                  <input
                    data-testid="external-imap-host"
                    value={imapHost}
                    onChange={(e) => setImapHost(e.target.value)}
                    placeholder="imap.yourdomain.com"
                    className="w-full bg-[#0e1a22] border border-[#8b5cf6]/25 px-3 py-2 text-sm outline-none focus:border-[#8b5cf6]/60 mono"
                  />
                </div>
                <div>
                  <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55 mb-1">port</div>
                  <input
                    data-testid="external-imap-port"
                    value={imapPort}
                    onChange={(e) => setImapPort(e.target.value.replace(/\D/g, ""))}
                    placeholder="993"
                    className="w-full bg-[#0e1a22] border border-[#8b5cf6]/25 px-3 py-2 text-sm outline-none focus:border-[#8b5cf6]/60 mono"
                  />
                </div>
              </div>

              <div className="bg-[#0e1a22]/60 border border-[#8b5cf6]/15 p-2.5" data-testid="imap-cheatsheet">
                <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/50 mb-1.5">// don't know your host? common providers</div>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { label: "Zoho Mail", host: "imappro.zoho.com", port: "993", help: "https://www.zoho.com/mail/help/imap-access.html" },
                    { label: "Google Workspace", host: "imap.gmail.com", port: "993", help: "https://support.google.com/a/answer/105694" },
                    { label: "GoDaddy", host: "imap.secureserver.net", port: "993", help: "https://www.godaddy.com/help/set-up-imap-and-smtp-b5654" },
                  ].map((p) => (
                    <button
                      key={p.label}
                      type="button"
                      data-testid={`imap-preset-${p.label.toLowerCase().replace(/\s/g, "-")}`}
                      onClick={() => { setImapHost(p.host); setImapPort(p.port); }}
                      className="flex items-center gap-1 px-2 py-1 mono text-[10px] border border-[#8b5cf6]/25 text-[#c7c1ff]/80 hover:border-[#8b5cf6]/60 hover:text-white transition-colors"
                    >
                      {p.label}
                      <a
                        href={p.help}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-[#3b82f6] hover:underline"
                        title={`How to find your ${p.label} IMAP settings`}
                      >
                        ?
                      </a>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          <div>
            <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55 mb-1">
              {provider === "custom" ? "imap password" : "gmail app password"}
            </div>
            <div className="flex items-center bg-[#0e1a22] border border-[#8b5cf6]/25 focus-within:border-[#8b5cf6]/60">
              <input
                data-testid="external-password"
                type={showPwd ? "text" : "password"}
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                placeholder="••••••••••••••••"
                className="flex-1 bg-transparent px-3 py-2 text-sm outline-none mono"
              />
              <button
                type="button"
                data-testid="external-show-password"
                onClick={() => setShowPwd(s => !s)}
                className="px-3 text-[#c7c1ff]/60 hover:text-[#8b5cf6]"
              >
                {showPwd ? "hide" : "show"}
              </button>
            </div>
            <div className="mono text-[9px] text-[#c7c1ff]/45 mt-1">
              {provider === "gmail" ? (
                <>// Gmail needs a 16-char <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-[#3b82f6] underline">app password</a> (requires 2-Step Verification), not your login password.</>
              ) : (
                <>// Ask your mail admin/host for the IMAP host, port &amp; password for this mailbox.</>
              )}
            </div>
          </div>

          {err && <div className="text-xs text-[#f87171] mono">{err}</div>}

          <div className="bg-[#8b5cf6]/8 border border-[#8b5cf6]/20 p-3 mono text-[10px] text-[#c7c1ff]/75 leading-relaxed">
            <div className="text-[#8b5cf6] uppercase tracking-widest mb-1">// what happens next</div>
            HEXA fetches your inbox into the unified mailbox. AI <span className="text-[#3b82f6]">summarizes</span> long threads and <span className="text-[#3b82f6]">drafts replies</span> for your review. <span className="text-[#34d399]">You</span> always click send.
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="btn-ghost text-[11px] uppercase tracking-widest mono">cancel</button>
            <button data-testid="external-connect-submit" disabled={busy} type="submit" className="btn-accent text-[11px] uppercase tracking-widest flex items-center gap-1.5 mono">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <LinkIcon size={12} />} Connect
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Compose({ account, replyTo, prefill, externalAccounts = [], onClose, onSent }) {
  const isReply = !!replyTo;
  // When replying to an external email, default the From to that external mailbox.
  const replyExtAcc = isReply && replyTo.external
    ? (externalAccounts.find(e => e.id === replyTo.external_account_id) || null)
    : null;
  const [fromId, setFromId] = useState(replyExtAcc ? replyExtAcc.id : "hexa");
  const isExternal = fromId !== "hexa";
  const fromExt = externalAccounts.find(e => e.id === fromId);
  const fromAddr = isExternal ? (fromExt?.email || account) : account;

  const [to, setTo] = useState(isReply ? replyTo.from_addr : "");
  const [subject, setSubject] = useState(isReply ? `Re: ${replyTo.subject.replace(/^(Re:\s*)+/i, "")}` : "");
  const [body, setBody] = useState(prefill || "");
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = React.useRef(null);
  const uploadAttachment = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setErr("Max attachment size is 5MB"); return; }
    const b64 = await new Promise((res, rej) => {
      const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
    });
    setAttachments(prev => [
      ...prev,
      { id: `local-${Date.now()}`, name: file.name, mime: file.type, size: file.size, data: b64 },
    ]);
  };
  // Voice dictation state (body)
  const [listening, setListening] = useState(false);
  const [voicePolishing, setVoicePolishing] = useState(false);
  const recogRef = React.useRef(null);
  // Voice dictation state (AI prompt)
  const [promptListening, setPromptListening] = useState(false);
  const promptRecogRef = React.useRef(null);
  // Schedule Meeting popover state
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [meetPurpose, setMeetPurpose] = useState("");
  const [meetDate, setMeetDate] = useState("");
  const [meetTime, setMeetTime] = useState("10:00");
  const [meetDuration, setMeetDuration] = useState(30);
  const [meetBusy, setMeetBusy] = useState(false);

  // Inline actions from compose — create a Task, Event or Note.
  const [taskOpen, setTaskOpen]   = useState(false);
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDesc, setTaskDesc]   = useState("");
  const [taskPriority, setTaskPriority] = useState("medium");
  const [taskDue, setTaskDue]     = useState("");
  const [taskBusy, setTaskBusy]   = useState(false);
  const [taskDone, setTaskDone]   = useState(false);

  const [eventOpen, setEventOpen] = useState(false);
  const [evTitle, setEvTitle]     = useState("");
  const [evDate, setEvDate]       = useState("");
  const [evTime, setEvTime]       = useState("10:00");
  const [evNotes, setEvNotes]     = useState("");
  const [evBusy, setEvBusy]       = useState(false);
  const [evDone, setEvDone]       = useState(false);

  const [noteOpen, setNoteOpen]   = useState(false);
  const [noteTitle, setNoteTitle] = useState("");
  const [noteBody, setNoteBody]   = useState("");
  const [noteBusy, setNoteBusy]   = useState(false);
  const [noteDone, setNoteDone]   = useState(false);

  // When any popover opens, prefill from the current compose fields so users
  // don't have to retype content that's already in the draft.
  const stripHtml = (html) => String(html || "").replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();

  const openTaskPopover = () => {
    setTaskTitle(t => t || subject || "");
    setTaskDesc(d => d || stripHtml(body).slice(0, 500));
    setTaskDone(false);
    setTaskOpen(v => !v);
    setEventOpen(false); setNoteOpen(false); setScheduleOpen(false);
  };
  const openEventPopover = () => {
    setEvTitle(t => t || subject || "");
    setEvNotes(n => n || stripHtml(body).slice(0, 500));
    if (!evDate) setEvDate(new Date().toISOString().slice(0, 10));
    setEvDone(false);
    setEventOpen(v => !v);
    setTaskOpen(false); setNoteOpen(false); setScheduleOpen(false);
  };
  const openNotePopover = () => {
    setNoteTitle(t => t || subject || "");
    setNoteBody(n => n || stripHtml(body));
    setNoteDone(false);
    setNoteOpen(v => !v);
    setTaskOpen(false); setEventOpen(false); setScheduleOpen(false);
  };

  const createTaskFromEmail = async () => {
    if (!taskTitle.trim()) return;
    setTaskBusy(true);
    try {
      await api.post("/tasks", {
        title: taskTitle.trim(),
        description: taskDesc,
        priority: taskPriority,
        status: "todo",
        due: taskDue || null,
        tags: ["from-email"],
      });
      setTaskDone(true);
      setTimeout(() => { setTaskOpen(false); setTaskDone(false); setTaskTitle(""); setTaskDesc(""); setTaskDue(""); }, 900);
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to create task");
    }
    setTaskBusy(false);
  };

  const createEventFromEmail = async () => {
    if (!evTitle.trim() || !evDate) return;
    setEvBusy(true);
    try {
      await api.post("/events", {
        title: evTitle.trim(),
        date: evDate,
        time: evTime || "",
        notes: evNotes || "",
      });
      setEvDone(true);
      setTimeout(() => { setEventOpen(false); setEvDone(false); setEvTitle(""); setEvNotes(""); }, 900);
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to create event");
    }
    setEvBusy(false);
  };

  const createNoteFromEmail = async () => {
    if (!noteTitle.trim() && !noteBody.trim()) return;
    setNoteBusy(true);
    try {
      await api.post("/documents", {
        kind: "note",
        title: noteTitle.trim() || "Untitled note",
        content: noteBody,
      });
      setNoteDone(true);
      setTimeout(() => { setNoteOpen(false); setNoteDone(false); setNoteTitle(""); setNoteBody(""); }, 900);
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to save note");
    }
    setNoteBusy(false);
  };

  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setErr("Voice not supported in this browser"); setTimeout(() => setErr(""), 3000); return; }
    if (listening) { try { recogRef.current?.stop(); } catch (_e) { /* ignore */ } setListening(false); return; }
    const r = new SR();
    r.lang = "en-US"; r.continuous = true; r.interimResults = true;
    let finalText = "";
    r.onresult = (ev) => {
      let interim = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const t = ev.results[i][0].transcript;
        if (ev.results[i].isFinal) finalText += t + " ";
        else interim += t;
      }
      setBody(() => (finalText + interim).trim());
    };
    r.onerror = () => setListening(false);
    r.onend = async () => {
      setListening(false);
      const raw = (finalText || "").trim();
      if (!raw) return;
      setVoicePolishing(true);
      try {
        const out = await mailAiApi.voicePolish(raw, "body");
        setBody(out.text || raw);
      } catch { setBody(raw); }
      setVoicePolishing(false);
    };
    recogRef.current = r;
    try { r.start(); setListening(true); } catch (_) { setErr("Could not start mic"); setTimeout(() => setErr(""), 3000); }
  };

  const startPromptVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setErr("Voice not supported in this browser"); setTimeout(() => setErr(""), 3000); return; }
    if (promptListening) { try { promptRecogRef.current?.stop(); } catch (_e) { /* ignore */ } setPromptListening(false); return; }
    const r = new SR();
    r.lang = "en-US"; r.continuous = false; r.interimResults = true;
    let finalText = "";
    r.onresult = (ev) => {
      let interim = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const t = ev.results[i][0].transcript;
        if (ev.results[i].isFinal) finalText += t + " ";
        else interim += t;
      }
      setAiPrompt(() => (finalText + interim).trim());
    };
    r.onerror = () => setPromptListening(false);
    r.onend = () => setPromptListening(false);
    promptRecogRef.current = r;
    try { r.start(); setPromptListening(true); } catch (_) { setErr("Could not start mic"); setTimeout(() => setErr(""), 3000); }
  };

  const aiCompose = async () => {
    if (!aiPrompt.trim() && !isReply) return;
    setAiBusy(true);
    try {
      if (isReply) {
        const { data } = await api.post("/mail/ai/reply", {
          original_subject: replyTo.subject,
          original_body: replyTo.body,
          intent: aiPrompt || "Polite, helpful reply.",
        });
        setBody(data.body);
      } else {
        const { data } = await api.post("/mail/ai/compose", { prompt: aiPrompt, tone: "professional" });
        setBody(data.body);
      }
    } catch { /* ignore */ }
    setAiBusy(false);
  };

  const openSchedule = () => {
    // Sensible defaults: tomorrow @ 10:00, purpose from subject.
    if (!meetPurpose) setMeetPurpose(subject || (isReply ? `Meeting: ${replyTo.subject}` : ""));
    if (!meetDate) {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      setMeetDate(d.toISOString().slice(0, 10));
    }
    setScheduleOpen(true);
  };

  const confirmSchedule = async () => {
    setErr("");
    if (!meetPurpose.trim()) { setErr("Purpose required"); return; }
    if (!meetDate || !meetTime) { setErr("Date & time required"); return; }
    setMeetBusy(true);
    const scheduled_at = `${meetDate}T${meetTime}:00`;
    try {
      // Build participants: include "To" if it's a HEXA address.
      const parts = [];
      const toClean = (to || "").trim();
      if (toClean.endsWith("@hexawebos.com")) parts.push(toClean);
      const meeting = await meetingsApi.create({
        title: meetPurpose,
        scheduled_at,
        duration_min: Number(meetDuration) || 30,
        host: account,
        participants: parts,
        agenda: body.slice(0, 400),
      });
      // Build a clickable meeting block to append to the body.
      const human = new Date(scheduled_at).toLocaleString([], {
        weekday: "short", month: "short", day: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
      const link = `${window.location.origin}/?app=meetings&room=${meeting.room_id || meeting.id}`;
      const block =
        `\n\n📅 Meeting Scheduled\n` +
        `• Purpose: ${meetPurpose}\n` +
        `• When: ${human} (${meetDuration} min)\n` +
        `• Join: ${link}\n`;
      setBody(b => (b || "") + block);
      setScheduleOpen(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not schedule meeting");
    }
    setMeetBusy(false);
  };

  const send = async () => {
    setErr("");
    setSending(true);
    try {
      if (isExternal) {
        // External outbound (Gmail/Outlook/IMAP) — any recipient allowed.
        const toFinal = (to || "").trim();
        if (!toFinal.includes("@")) { setErr("Enter a valid recipient email."); setSending(false); return; }
        if (!subject) { setErr("Subject required."); setSending(false); return; }
        await externalMailApi.send(fromExt.id, toFinal, subject, body, attachments);
        onSent();
      } else {
        // Internal HEXA-to-HEXA — normalize shorthand and enforce domain.
        let toFinal = (to || "").trim();
        if (toFinal.startsWith("@")) toFinal = `${toFinal.slice(1)}@hexawebos.com`;
        else if (toFinal && !toFinal.includes("@")) toFinal = `${toFinal}@hexawebos.com`;
        if (!toFinal.endsWith("@hexawebos.com")) {
          setErr("Only @hexawebos.com recipients allowed. Switch From to an external mailbox to send outside HEXA.");
          setSending(false);
          return;
        }
        if (!subject) { setErr("Subject required."); setSending(false); return; }
        await api.post("/mail/messages/send", {
          from_addr: fromAddr,
          to_addr: toFinal,
          subject,
          body,
          thread_id: replyTo?.thread_id,
          in_reply_to: replyTo?.id,
          attachments,
        });
        onSent();
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Send failed.");
    }
    setSending(false);
  };

  return (
    <div className="absolute inset-0 bg-black/70 flex items-stretch md:items-center justify-center z-20 p-0 md:p-6" onClick={onClose}>
      <div className="glass w-full md:max-w-2xl md:rounded-xl flex flex-col h-full md:h-auto md:max-h-[88%]" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-2 border-b border-white/10">
          <div className="text-sm font-medium">{isReply ? "Reply" : "New Message"}</div>
          <button onClick={onClose} className="btn-ghost p-1"><X size={12} /></button>
        </div>
        <div className="p-3 space-y-2 flex-1 flex flex-col overflow-y-auto">
          <div className="flex items-center gap-2 text-xs">
            <span className="w-12 text-white/40">From</span>
            <select
              data-testid="compose-from-select"
              value={fromId}
              onChange={(e) => setFromId(e.target.value)}
              className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white/85 mono text-[11px]"
              style={{ colorScheme: "dark" }}
            >
              <option value="hexa" style={{ background: "#ffffff", color: "#0f172a" }}>{shortAddr(account)} · HEXA</option>
              {externalAccounts.map(ea => (
                <option key={ea.id} value={ea.id} style={{ background: "#ffffff", color: "#0f172a" }}>{shortAddr(ea.email)} · {ea.provider_label}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="w-12 text-white/40">To</span>
            <input
              data-testid="compose-to"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder={isExternal ? "anyone@anywhere.com" : "@navjot or someone@hexawebos.com"}
              className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1.5"
            />
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="w-12 text-white/40">Subject</span>
            <input data-testid="compose-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1.5" />
          </div>
          <div className="flex-1 flex flex-col gap-1 min-h-[160px] relative">
            <textarea
              data-testid="compose-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write your email…"
              className="flex-1 bg-white/5 border border-white/10 rounded p-3 text-sm resize-none min-h-[160px]"
            />
            <button
              type="button"
              data-testid="compose-voice"
              onClick={startVoice}
              title={listening ? "Stop dictating" : "Voice dictate (browser mic)"}
              className="absolute top-2 right-2 p-1.5 rounded-full transition"
              style={{
                background: listening ? "rgba(248,113,113,0.20)" : "rgba(167,139,250,0.15)",
                border: `1px solid ${listening ? "rgba(248,113,113,0.6)" : "rgba(167,139,250,0.45)"}`,
                color: listening ? "#f87171" : "#c7c1ff",
              }}
            >
              {listening ? <MicOff size={12} /> : <Mic size={12} />}
            </button>
            {(listening || voicePolishing) && (
              <div className="absolute bottom-2 right-2 text-[9px] mono uppercase tracking-widest text-[#a78bfa] flex items-center gap-1">
                {voicePolishing ? (
                  <><Loader2 size={10} className="animate-spin" /> polishing…</>
                ) : (
                  <><span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" /> listening</>
                )}
              </div>
            )}
          </div>
          {attachments.length > 0 && (
            <div className="space-y-1" data-testid="compose-attachments-list">
              {attachments.map(a => (
                <div key={a.id} className="flex items-center gap-2 px-2 py-1.5 rounded"
                     style={{ background: "rgba(255,122,46,0.08)", border: "1px solid rgba(255,122,46,0.28)" }}>
                  <Paperclip size={11} className="text-[#ff7a2e] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] text-white/90 truncate">{a.name}</div>
                    <div className="mono text-[9px] uppercase tracking-widest text-white/45">{Math.round((a.size || 0) / 1024)} KB</div>
                  </div>
                  <button
                    type="button"
                    data-testid={`compose-attach-remove-${a.id}`}
                    onClick={() => setAttachments(prev => prev.filter(x => x.id !== a.id))}
                    className="text-white/50 hover:text-red-400 p-1"
                  >
                    <X size={11} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2 relative">
            <button
              type="button"
              data-testid="compose-attach-btn"
              onClick={() => fileInputRef.current?.click()}
              className="btn-ghost text-xs flex items-center gap-1"
              style={{ borderColor: "rgba(255,122,46,0.45)", color: "#ff7a2e" }}
            >
              <Paperclip size={11} /> Attach
            </button>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              data-testid="compose-attach-input"
              onChange={(e) => { uploadAttachment(e.target.files?.[0]); e.target.value = ""; }}
            />
            <button
              type="button"
              data-testid="compose-schedule-meeting"
              onClick={openSchedule}
              className="btn-ghost text-xs flex items-center gap-1"
              style={{ borderColor: "rgba(96,165,250,0.45)", color: "#60a5fa" }}
            >
              <CalendarPlus size={11} /> Schedule Meeting
            </button>
            <button
              type="button"
              data-testid="compose-add-task"
              onClick={openTaskPopover}
              className="btn-ghost text-xs flex items-center gap-1"
              style={{ borderColor: "rgba(34,197,94,0.45)", color: "#22c55e" }}
            >
              <ListChecks size={11} /> Task
            </button>
            <button
              type="button"
              data-testid="compose-add-event"
              onClick={openEventPopover}
              className="btn-ghost text-xs flex items-center gap-1"
              style={{ borderColor: "rgba(168,85,247,0.45)", color: "#a855f7" }}
            >
              <CalendarIcon size={11} /> Event
            </button>
            <button
              type="button"
              data-testid="compose-add-note"
              onClick={openNotePopover}
              className="btn-ghost text-xs flex items-center gap-1"
              style={{ borderColor: "rgba(251,191,36,0.45)", color: "#fbbf24" }}
            >
              <StickyNote size={11} /> Note
            </button>
            <span className="text-[10px] text-white/40">Max 5MB per file</span>

            {taskOpen && (
              <div
                data-testid="compose-task-popover"
                className="absolute bottom-full left-0 mb-2 z-30 w-[340px] panel-strong p-3 space-y-2"
                style={{ background: "#ffffff", border: "1px solid rgba(34,197,94,0.35)" }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between">
                  <div className="mono text-[10px] uppercase tracking-widest text-emerald-400 flex items-center gap-1">
                    <ListChecks size={11} /> // create task
                  </div>
                  <button onClick={() => setTaskOpen(false)} className="text-white/50 hover:text-white"><X size={10} /></button>
                </div>
                <div>
                  <div className="mono text-[9px] uppercase tracking-widest text-white/50 mb-1">Title *</div>
                  <input value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)} placeholder="What needs to be done?" className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs" data-testid="task-title-input" />
                </div>
                <div>
                  <div className="mono text-[9px] uppercase tracking-widest text-white/50 mb-1">Description</div>
                  <textarea value={taskDesc} onChange={(e) => setTaskDesc(e.target.value)} rows={2} className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs resize-y" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="mono text-[9px] uppercase tracking-widest text-white/50 mb-1">Priority</div>
                    <select value={taskPriority} onChange={(e) => setTaskPriority(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs">
                      <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="urgent">Urgent</option>
                    </select>
                  </div>
                  <div>
                    <div className="mono text-[9px] uppercase tracking-widest text-white/50 mb-1">Due</div>
                    <input type="date" value={taskDue} onChange={(e) => setTaskDue(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs" />
                  </div>
                </div>
                <button onClick={createTaskFromEmail} disabled={taskBusy || !taskTitle.trim()} data-testid="task-create-btn" className="w-full text-xs px-3 py-1.5 rounded bg-emerald-500 text-white font-semibold hover:bg-emerald-600 disabled:opacity-60 flex items-center justify-center gap-1">
                  {taskBusy ? <Loader2 size={11} className="animate-spin" /> : (taskDone ? <CheckSquare size={11} /> : <Plus size={11} />)}
                  {taskDone ? "Task created" : "Create task"}
                </button>
              </div>
            )}

            {eventOpen && (
              <div
                data-testid="compose-event-popover"
                className="absolute bottom-full left-0 mb-2 z-30 w-[340px] panel-strong p-3 space-y-2"
                style={{ background: "#ffffff", border: "1px solid rgba(168,85,247,0.35)" }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between">
                  <div className="mono text-[10px] uppercase tracking-widest text-purple-400 flex items-center gap-1">
                    <CalendarIcon size={11} /> // create event
                  </div>
                  <button onClick={() => setEventOpen(false)} className="text-white/50 hover:text-white"><X size={10} /></button>
                </div>
                <div>
                  <div className="mono text-[9px] uppercase tracking-widest text-white/50 mb-1">Title *</div>
                  <input value={evTitle} onChange={(e) => setEvTitle(e.target.value)} placeholder="Event name" className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs" data-testid="event-title-input" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="mono text-[9px] uppercase tracking-widest text-white/50 mb-1">Date *</div>
                    <input type="date" value={evDate} onChange={(e) => setEvDate(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs" />
                  </div>
                  <div>
                    <div className="mono text-[9px] uppercase tracking-widest text-white/50 mb-1">Time</div>
                    <input type="time" value={evTime} onChange={(e) => setEvTime(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs" />
                  </div>
                </div>
                <div>
                  <div className="mono text-[9px] uppercase tracking-widest text-white/50 mb-1">Notes</div>
                  <textarea value={evNotes} onChange={(e) => setEvNotes(e.target.value)} rows={2} className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs resize-y" />
                </div>
                <button onClick={createEventFromEmail} disabled={evBusy || !evTitle.trim() || !evDate} data-testid="event-create-btn" className="w-full text-xs px-3 py-1.5 rounded bg-purple-500 text-white font-semibold hover:bg-purple-600 disabled:opacity-60 flex items-center justify-center gap-1">
                  {evBusy ? <Loader2 size={11} className="animate-spin" /> : (evDone ? <CheckSquare size={11} /> : <Plus size={11} />)}
                  {evDone ? "Event created" : "Create event"}
                </button>
              </div>
            )}

            {noteOpen && (
              <div
                data-testid="compose-note-popover"
                className="absolute bottom-full left-0 mb-2 z-30 w-[340px] panel-strong p-3 space-y-2"
                style={{ background: "#ffffff", border: "1px solid rgba(251,191,36,0.35)" }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between">
                  <div className="mono text-[10px] uppercase tracking-widest text-amber-400 flex items-center gap-1">
                    <StickyNote size={11} /> // save note
                  </div>
                  <button onClick={() => setNoteOpen(false)} className="text-white/50 hover:text-white"><X size={10} /></button>
                </div>
                <div>
                  <div className="mono text-[9px] uppercase tracking-widest text-white/50 mb-1">Title</div>
                  <input value={noteTitle} onChange={(e) => setNoteTitle(e.target.value)} placeholder="Note title" className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs" data-testid="note-title-input" />
                </div>
                <div>
                  <div className="mono text-[9px] uppercase tracking-widest text-white/50 mb-1">Content</div>
                  <textarea value={noteBody} onChange={(e) => setNoteBody(e.target.value)} rows={4} className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs resize-y" />
                </div>
                <button onClick={createNoteFromEmail} disabled={noteBusy || (!noteTitle.trim() && !noteBody.trim())} data-testid="note-create-btn" className="w-full text-xs px-3 py-1.5 rounded bg-amber-500 text-white font-semibold hover:bg-amber-600 disabled:opacity-60 flex items-center justify-center gap-1">
                  {noteBusy ? <Loader2 size={11} className="animate-spin" /> : (noteDone ? <CheckSquare size={11} /> : <Plus size={11} />)}
                  {noteDone ? "Note saved" : "Save note"}
                </button>
              </div>
            )}

            {scheduleOpen && (
              <div
                data-testid="schedule-meeting-popover"
                className="absolute bottom-full left-0 mb-2 z-30 w-[340px] panel-strong p-3 space-y-2"
                style={{ background: "#ffffff", border: "1px solid rgba(96,165,250,0.35)" }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between">
                  <div className="mono text-[10px] uppercase tracking-widest text-[#60a5fa] flex items-center gap-1">
                    <CalendarPlus size={11} /> // schedule meeting
                  </div>
                  <button onClick={() => setScheduleOpen(false)} className="text-white/50 hover:text-white"><X size={10} /></button>
                </div>
                <div>
                  <div className="mono text-[9px] uppercase tracking-widest text-white/50 mb-1">Purpose</div>
                  <input
                    data-testid="meeting-purpose"
                    value={meetPurpose}
                    onChange={(e) => setMeetPurpose(e.target.value)}
                    placeholder="Q3 roadmap review"
                    className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="mono text-[9px] uppercase tracking-widest text-white/50 mb-1">Date</div>
                    <input
                      data-testid="meeting-date"
                      type="date"
                      value={meetDate}
                      onChange={(e) => setMeetDate(e.target.value)}
                      className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs"
                    />
                  </div>
                  <div>
                    <div className="mono text-[9px] uppercase tracking-widest text-white/50 mb-1">Time</div>
                    <input
                      data-testid="meeting-time"
                      type="time"
                      value={meetTime}
                      onChange={(e) => setMeetTime(e.target.value)}
                      className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs"
                    />
                  </div>
                </div>
                <div>
                  <div className="mono text-[9px] uppercase tracking-widest text-white/50 mb-1">Duration (min)</div>
                  <select
                    data-testid="meeting-duration"
                    value={meetDuration}
                    onChange={(e) => setMeetDuration(Number(e.target.value))}
                    className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs"
                    style={{ colorScheme: "dark" }}
                  >
                    <option value={15} style={{ background: "#ffffff", color: "#fff" }}>15</option>
                    <option value={30} style={{ background: "#ffffff", color: "#fff" }}>30</option>
                    <option value={45} style={{ background: "#ffffff", color: "#fff" }}>45</option>
                    <option value={60} style={{ background: "#ffffff", color: "#fff" }}>60</option>
                    <option value={90} style={{ background: "#ffffff", color: "#fff" }}>90</option>
                  </select>
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <button onClick={() => setScheduleOpen(false)} className="btn-ghost text-[11px]">Cancel</button>
                  <button
                    data-testid="meeting-confirm"
                    onClick={confirmSchedule}
                    disabled={meetBusy}
                    className="btn-accent text-[11px] flex items-center gap-1"
                  >
                    {meetBusy ? <Loader2 size={11} className="animate-spin" /> : <CalendarPlus size={11} />} Add to email
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="border border-white/10 rounded p-2 bg-black/30">
            <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1.5 flex items-center gap-1">
              <Sparkles size={10} color="#ff7a2e" /> {isReply ? "Reply intent (optional)" : "AI compose prompt"}
            </div>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <input
                  data-testid="compose-ai-prompt"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder={isReply ? "e.g. politely decline" : "e.g. ask for a meeting next Tuesday"}
                  className="w-full bg-white/5 border border-white/10 rounded pl-2 pr-8 py-1.5 text-xs"
                />
                <button
                  type="button"
                  data-testid="compose-ai-prompt-voice"
                  onClick={startPromptVoice}
                  title={promptListening ? "Stop" : "Voice prompt"}
                  className="absolute top-1/2 right-1.5 -translate-y-1/2 p-1 rounded-full transition"
                  style={{
                    background: promptListening ? "rgba(248,113,113,0.20)" : "transparent",
                    border: `1px solid ${promptListening ? "rgba(248,113,113,0.6)" : "rgba(167,139,250,0.45)"}`,
                    color: promptListening ? "#f87171" : "#c7c1ff",
                  }}
                >
                  {promptListening ? <MicOff size={10} /> : <Mic size={10} />}
                </button>
              </div>
              <button data-testid="compose-ai-run" disabled={aiBusy} onClick={aiCompose} className="btn-ghost text-xs flex items-center gap-1">
                {aiBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Write
              </button>
            </div>
          </div>
          {err && <div className="text-xs text-red-400">{err}</div>}
        </div>
        <div className="px-3 py-2 border-t border-white/10 flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost text-xs">Cancel</button>
          <button data-testid="compose-send" disabled={sending} onClick={send} className="btn-accent text-xs flex items-center gap-1">
            {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send
          </button>
        </div>
      </div>
    </div>
  );
}


function ExternalReplyPanel({ msg, onSent }) {
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");

  const aiDraft = async () => {
    setBusy(true); setStatus("");
    try {
      const { data } = await api.post("/mail/ai/reply", {
        original_subject: msg.subject,
        original_body: msg.body,
        intent: "Helpful, concise reply matching my voice.",
      });
      setDraft(data.body || "");
    } catch {
      setStatus("× draft failed");
    }
    setBusy(false);
  };

  const send = async () => {
    if (!draft.trim()) { setStatus("write something first"); return; }
    setBusy(true); setStatus("");
    try {
      await externalMailApi.sendReply(msg.id, draft, "");
      setStatus("✓ sent");
      setDraft("");
      onSent?.();
    } catch {
      setStatus("× send failed");
    }
    setBusy(false);
    setTimeout(() => setStatus(""), 4000);
  };

  return (
    <div className="mx-3 mb-3 p-3 border" style={{ background: "rgba(139,92,246,0.05)", borderColor: "rgba(139,92,246,0.25)" }} data-testid="external-reply-panel">
      <div className="flex items-center gap-1.5 mono text-[10px] uppercase tracking-widest text-[#8b5cf6] mb-2">
        <LinkIcon size={11} />
        <span>// reply via {msg.external_provider} ({msg.external_email})</span>
      </div>
      <textarea
        data-testid="external-reply-textarea"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Type your reply, or click AI Draft to autofill…"
        rows={5}
        className="w-full bg-[#0e1a22] border border-[#8b5cf6]/25 px-2 py-1.5 text-xs outline-none focus:border-[#8b5cf6]/60 mono"
      />
      <div className="flex items-center gap-2 mt-2">
        <button
          data-testid="external-ai-draft"
          onClick={aiDraft}
          disabled={busy}
          className="btn-ghost text-[10px] uppercase tracking-widest flex items-center gap-1 mono"
        >
          {busy ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />} AI Draft
        </button>
        <button
          data-testid="external-send-reply"
          onClick={send}
          disabled={busy || !draft.trim()}
          className="btn-accent text-[10px] uppercase tracking-widest flex items-center gap-1 mono"
        >
          {busy ? <Loader2 size={10} className="animate-spin" /> : <Send size={10} />} Send Reply
        </button>
        {status && (
          <span className="mono text-[9px] uppercase tracking-widest text-[#3b82f6] ml-auto">{status}</span>
        )}
      </div>
      <div className="mono text-[9px] text-[#c7c1ff]/40 mt-1.5">
        // AI drafts · YOU send. Outbound queued via {msg.external_provider} smtp gateway.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// "New" split button — Outlook-style dropdown to quick-create
// notes, tasks, events, contacts, bookmarks, bookings from Mail.
// ─────────────────────────────────────────────────────────────
function NewSplitButton({ onNewMail, openApp, variant = "sidebar", linkedEmailId = null, onCreated = null }) {
  const [open, setOpen] = useState(false);
  const [quickModal, setQuickModal] = useState(null); // "contact" | "bookmark" | "task" | "event" | null
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handle = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", handle);
    return () => window.removeEventListener("mousedown", handle);
  }, [open]);

  const items = [
    { key: "mail",     label: "New Mail",     hint: "compose email",    icon: MailIcon,       action: () => onNewMail() },
    { key: "note",     label: "New Note",     hint: "quick note",       icon: StickyNote,     action: () => openAppById("notes") },
    { key: "task",     label: "New Task",     hint: linkedEmailId ? "add to-do · linked to this email" : "add to-do",  icon: ListChecks,     action: () => setQuickModal("task") },
    { key: "event",    label: "New Event",    hint: linkedEmailId ? "schedule · linked to this email" : "schedule",   icon: CalendarIcon,   action: () => setQuickModal("event") },
    { key: "contact",  label: "New Contact",  hint: "save contact",     icon: UserPlus,       action: () => setQuickModal("contact") },
    { key: "bookmark", label: "New Bookmark", hint: "save url",         icon: Bookmark,       action: () => setQuickModal("bookmark") },
    { key: "booking",  label: "New Booking",  hint: "book a meeting",   icon: ClockIcon,      action: () => openAppById("meetings") },
  ];

  // Look up the full app registration object and pass it to openApp — the
  // window manager expects `{ id, title, Component, defaultSize, ... }`.
  const openAppById = (id) => {
    const app = APPS.find((a) => a.id === id);
    if (app) openApp(app);
    else console.warn(`[Mail] Unknown app id: ${id}`);
  };

  const [primary, ...rest] = items;

  return (
    <div className="relative" ref={ref}>
      <div className={`flex items-stretch ${variant === "sidebar" ? "w-full" : ""}`}>
        <button
          data-testid={variant === "sidebar" ? "mail-compose" : "mail-compose-mobile"}
          onClick={() => { setOpen(false); primary.action(); }}
          className="btn-accent text-xs flex items-center justify-center gap-1 px-2.5 py-1.5 flex-1 rounded-r-none"
          style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
        >
          <Plus size={12} /> {variant === "mobile" ? "New" : "New Mail"}
        </button>
        <button
          data-testid="mail-new-dropdown-toggle"
          onClick={() => setOpen((v) => !v)}
          className="btn-accent text-xs flex items-center justify-center px-2 py-1.5 border-l-0"
          style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, marginLeft: -1 }}
          title="More new..."
          aria-expanded={open}
        >
          <ChevronDown size={12} />
        </button>
      </div>

      {open && (
        <div
          data-testid="mail-new-dropdown"
          className="absolute z-[70] mt-1 w-56 rounded overflow-hidden"
          style={{
            background: "#ffffff",
            border: "1px solid rgba(139,92,246,0.35)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.55)",
            left: variant === "mobile" ? "auto" : 0,
            right: variant === "mobile" ? 0 : "auto",
          }}
        >
          <div className="px-2.5 py-1.5 border-b border-white/8 mono text-[9px] uppercase tracking-[0.28em] text-white/45">
            // create new
          </div>
          {rest.map(({ key, label, hint, icon: Icon, action }) => (
            <button
              key={key}
              data-testid={`mail-new-${key}`}
              onClick={() => { setOpen(false); action(); }}
              className="w-full text-left px-2.5 py-2 flex items-center gap-2.5 hover:bg-white/6 transition"
            >
              <Icon size={13} className="text-[#a78bfa] flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-[12px] text-white/90">{label}</div>
                <div className="mono text-[9px] uppercase tracking-widest text-white/40">{hint}</div>
              </div>
            </button>
          ))}
        </div>
      )}

      {quickModal === "contact" && (
        <QuickContactModal onClose={() => setQuickModal(null)} />
      )}
      {quickModal === "bookmark" && (
        <QuickBookmarkModal onClose={() => setQuickModal(null)} />
      )}
      {quickModal === "task" && (
        <QuickTaskModal
          linkedEmailId={linkedEmailId}
          onClose={() => setQuickModal(null)}
          onCreated={(item) => { setQuickModal(null); onCreated && onCreated("task", item); }}
        />
      )}
      {quickModal === "event" && (
        <QuickEventModal
          linkedEmailId={linkedEmailId}
          onClose={() => setQuickModal(null)}
          onCreated={(item) => { setQuickModal(null); onCreated && onCreated("event", item); }}
        />
      )}
    </div>
  );
}

function QuickContactModal({ onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [saved, setSaved] = useState(false);

  const save = async () => {
    if (!name.trim() && !email.trim()) { setErr("Name or email is required."); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/contacts", { name, email, phone, company });
      setSaved(true);
      setTimeout(() => onClose(), 900);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <QuickModalShell title="New Contact" icon={UserPlus} onClose={onClose}>
      <QuickField label="Name">
        <input data-testid="quick-contact-name" value={name} onChange={(e) => setName(e.target.value)} className="quick-input" autoFocus placeholder="Jane Doe" />
      </QuickField>
      <QuickField label="Email">
        <input data-testid="quick-contact-email" value={email} onChange={(e) => setEmail(e.target.value)} className="quick-input" placeholder="jane@company.com" />
      </QuickField>
      <QuickField label="Phone">
        <input data-testid="quick-contact-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="quick-input" placeholder="+91 98765 43210" />
      </QuickField>
      <QuickField label="Company">
        <input data-testid="quick-contact-company" value={company} onChange={(e) => setCompany(e.target.value)} className="quick-input" placeholder="Acme Inc." />
      </QuickField>
      {err && <div className="text-[11px] text-red-300 mono">{err}</div>}
      {saved && <div className="text-[11px] text-emerald-300 mono">// contact saved</div>}
      <QuickActions onCancel={onClose} onSave={save} busy={busy} testId="quick-contact-save" />
    </QuickModalShell>
  );
}

function QuickBookmarkModal({ onClose }) {
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [saved, setSaved] = useState(false);

  const save = async () => {
    if (!url.trim()) { setErr("URL is required."); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/bookmarks", { title, url, note });
      setSaved(true);
      setTimeout(() => onClose(), 900);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <QuickModalShell title="New Bookmark" icon={Bookmark} onClose={onClose}>
      <QuickField label="URL">
        <input data-testid="quick-bookmark-url" value={url} onChange={(e) => setUrl(e.target.value)} autoFocus className="quick-input" placeholder="https://example.com/article" />
      </QuickField>
      <QuickField label="Title">
        <input data-testid="quick-bookmark-title" value={title} onChange={(e) => setTitle(e.target.value)} className="quick-input" placeholder="Optional friendly name" />
      </QuickField>
      <QuickField label="Note">
        <textarea data-testid="quick-bookmark-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="quick-input resize-y" placeholder="Why is this useful?" />
      </QuickField>
      {err && <div className="text-[11px] text-red-300 mono">{err}</div>}
      {saved && <div className="text-[11px] text-emerald-300 mono">// bookmark saved</div>}
      <QuickActions onCancel={onClose} onSave={save} busy={busy} testId="quick-bookmark-save" />
    </QuickModalShell>
  );
}

function QuickModalShell({ title, icon: Icon, children, onClose }) {
  return (
    <div className="fixed inset-0 z-[400] bg-black/60 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-[min(460px,94vw)] rounded-xl bg-[#0c1224] border border-white/10 overflow-hidden"
        style={{ boxShadow: "0 30px 80px rgba(0,0,0,0.6)" }}
      >
        <div className="px-4 py-2.5 border-b border-white/8 flex items-center gap-2">
          <Icon size={13} className="text-[#a78bfa]" />
          <div className="mono text-[11px] uppercase tracking-[0.28em] text-white/85">{title}</div>
          <button onClick={onClose} className="ml-auto text-white/50 hover:text-white p-1">
            <X size={14} />
          </button>
        </div>
        <div className="p-4 space-y-3">{children}</div>
      </div>
      <style>{`
        .quick-input {
          width: 100%;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.10);
          padding: 8px 10px;
          border-radius: 4px;
          font-size: 12.5px;
          color: rgba(255,255,255,0.95);
          outline: none;
        }
        .quick-input:focus { border-color: rgba(167,139,250,0.5); }
      `}</style>
    </div>
  );
}

function QuickField({ label, children }) {
  return (
    <label className="block">
      <div className="mono text-[9px] uppercase tracking-[0.28em] text-white/45 mb-1">{label}</div>
      {children}
    </label>
  );
}

function QuickActions({ onCancel, onSave, busy, testId }) {
  return (
    <div className="flex items-center justify-end gap-2 pt-1">
      <button onClick={onCancel} className="btn-ghost text-xs px-3">Cancel</button>
      <button data-testid={testId} onClick={onSave} disabled={busy} className="btn-accent text-xs px-4 flex items-center gap-1.5">
        {busy ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />}
        Save
      </button>
    </div>
  );
}

function QuickTaskModal({ onClose, onCreated, linkedEmailId = null }) {
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [priority, setPriority] = useState("normal");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    if (!title.trim()) { setErr("Title is required."); return; }
    setBusy(true); setErr("");
    try {
      const payload = { title, description, priority, due };
      if (linkedEmailId) payload.linked_email_id = linkedEmailId;
      const r = await api.post("/tasks", payload);
      onCreated && onCreated(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <QuickModalShell title={linkedEmailId ? "New Task · from this email" : "New Task"} icon={ListChecks} onClose={onClose}>
      <QuickField label="Title">
        <input
          data-testid="quick-task-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="quick-input"
          autoFocus
          placeholder="e.g. Follow up with Priya about proposal"
        />
      </QuickField>
      <div className="grid grid-cols-2 gap-3">
        <QuickField label="Due">
          <input
            data-testid="quick-task-due"
            type="date"
            value={due}
            onChange={(e) => setDue(e.target.value)}
            className="quick-input"
          />
        </QuickField>
        <QuickField label="Priority">
          <select
            data-testid="quick-task-priority"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            className="quick-input"
          >
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </QuickField>
      </div>
      <QuickField label="Description">
        <textarea
          data-testid="quick-task-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          className="quick-input resize-y"
          placeholder="Optional notes / context"
        />
      </QuickField>
      {linkedEmailId && (
        <div className="mono text-[10px] text-emerald-300/85 uppercase tracking-[0.24em] flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
          will link to this email
        </div>
      )}
      {err && <div className="text-[11px] text-red-300 mono">{err}</div>}
      <QuickActions onCancel={onClose} onSave={save} busy={busy} testId="quick-task-save" />
    </QuickModalShell>
  );
}

function QuickEventModal({ onClose, onCreated, linkedEmailId = null }) {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [time, setTime] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    if (!title.trim()) { setErr("Title is required."); return; }
    if (!date) { setErr("Date is required."); return; }
    setBusy(true); setErr("");
    try {
      const payload = { title, date, time, notes };
      if (linkedEmailId) payload.linked_email_id = linkedEmailId;
      const r = await api.post("/events", payload);
      onCreated && onCreated(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <QuickModalShell title={linkedEmailId ? "New Event · from this email" : "New Event"} icon={CalendarIcon} onClose={onClose}>
      <QuickField label="Title">
        <input
          data-testid="quick-event-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="quick-input"
          autoFocus
          placeholder="e.g. Design review with Priya"
        />
      </QuickField>
      <div className="grid grid-cols-2 gap-3">
        <QuickField label="Date">
          <input
            data-testid="quick-event-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="quick-input"
          />
        </QuickField>
        <QuickField label="Time">
          <input
            data-testid="quick-event-time"
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="quick-input"
          />
        </QuickField>
      </div>
      <QuickField label="Notes">
        <textarea
          data-testid="quick-event-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="quick-input resize-y"
          placeholder="Location, agenda, links…"
        />
      </QuickField>
      {linkedEmailId && (
        <div className="mono text-[10px] text-emerald-300/85 uppercase tracking-[0.24em] flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
          will link to this email
        </div>
      )}
      {err && <div className="text-[11px] text-red-300 mono">{err}</div>}
      <QuickActions onCancel={onClose} onSave={save} busy={busy} testId="quick-event-save" />
    </QuickModalShell>
  );
}

/* ------------------------------------------------------------------
   LinkedItemsPanel — renders inside a mail message view.
   Fetches tasks + events with `linked_email_id === messageId` and
   also exposes inline create-buttons so the entire flow lives
   INSIDE the email (no redirect to Tasks / Calendar apps).
------------------------------------------------------------------ */
// eslint-disable-next-line no-unused-vars
export function LinkedItemsPanel({ messageId, openApp, creating, setCreating }) {
  const [tasks, setTasks] = useState([]);
  const [events, setEvents] = useState([]);

  const refresh = React.useCallback(async () => {
    if (!messageId) return;
    try {
      const [t, e] = await Promise.all([
        api.get("/tasks",  { params: { linked_email_id: messageId } }),
        api.get("/events", { params: { linked_email_id: messageId } }),
      ]);
      setTasks(Array.isArray(t.data) ? t.data : []);
      setEvents(Array.isArray(e.data) ? e.data : []);
    } catch { /* ignore */ }
  }, [messageId]);

  useEffect(() => { refresh(); }, [refresh]);

  const total = tasks.length + events.length;

  if (total === 0) return null; // Support #301778: hide empty linked panel
  return (
    <div
      data-testid="mail-linked-items"
      className="mt-4 border rounded-md overflow-hidden"
      style={{ borderColor: "rgba(139,92,246,0.25)", background: "#f8fafc" }}
    >
      <div
        className="px-3 py-2 flex items-center justify-between border-b"
        style={{ borderColor: "rgba(139,92,246,0.18)", background: "rgba(139,92,246,0.08)" }}
      >
        <div className="mono text-[10px] uppercase tracking-[0.28em] text-[#c7c1ff]/80 flex items-center gap-2">
          <Sparkles size={11} className="text-[#a78bfa]" />
          linked to this email
          {total > 0 && (
            <span
              className="ml-0.5 px-1.5 py-0.5 text-[9px] font-bold mono"
              style={{ background: "#a78bfa", color: "#0b1014", lineHeight: 1 }}
            >{total}</span>
          )}
        </div>
      </div>

      {total === 0 && (
        <div className="px-3 py-3 mono text-[10.5px] uppercase tracking-[0.22em] text-white/40">
          // no linked tasks or events yet
        </div>
      )}

      {tasks.length > 0 && (
        <div className="px-3 py-2 border-b border-white/5">
          <div className="mono text-[9px] uppercase tracking-[0.28em] text-emerald-300/70 mb-1">
            tasks · {tasks.length}
          </div>
          <ul className="space-y-1">
            {tasks.map((t) => (
              <li key={t.id} className="flex items-center gap-2 text-[12.5px]">
                <ListChecks size={11} className="text-emerald-300 flex-shrink-0" />
                <span className="truncate flex-1">{t.title}</span>
                {t.due && (
                  <span className="mono text-[9px] uppercase tracking-widest text-white/40">due {t.due}</span>
                )}
                <span
                  className="mono text-[9px] uppercase tracking-widest px-1.5"
                  style={{
                    background: t.status === "done" ? "rgba(52,211,153,0.15)" : "rgba(255,255,255,0.06)",
                    color: t.status === "done" ? "#34d399" : "rgba(255,255,255,0.55)",
                  }}
                >
                  {t.status || "todo"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {events.length > 0 && (
        <div className="px-3 py-2">
          <div className="mono text-[9px] uppercase tracking-[0.28em] text-[#a78bfa]/80 mb-1">
            events · {events.length}
          </div>
          <ul className="space-y-1">
            {events.map((e) => (
              <li key={e.id} className="flex items-center gap-2 text-[12.5px]">
                <CalendarIcon size={11} className="text-[#a78bfa] flex-shrink-0" />
                <span className="truncate flex-1">{e.title}</span>
                <span className="mono text-[9px] uppercase tracking-widest text-white/40">
                  {e.date}{e.time ? ` · ${e.time}` : ""}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {creating === "task" && (
        <QuickTaskModal
          linkedEmailId={messageId}
          onClose={() => setCreating(null)}
          onCreated={() => { setCreating(null); refresh(); }}
        />
      )}
      {creating === "event" && (
        <QuickEventModal
          linkedEmailId={messageId}
          onClose={() => setCreating(null)}
          onCreated={() => { setCreating(null); refresh(); }}
        />
      )}
    </div>
  );
}


